import {
  useAssetDropdownInput,
  useGetAssetTypeOptions,
} from "#src/batteries-included-components/FilterAreas/assetFilters.helpers";
import {
  FilterArea,
  useFilterAreaContentContext,
  useFilterAreaContext,
} from "#src/components/FilterArea";
import { FilterDrawer } from "#src/components/FilterDrawer";
import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  Accordion,
  DateSelectorInput,
  DropdownInput,
  PillToggleGroup,
  PillToggleVariants,
  TextInput,
  type StorageKeys,
} from "@validereinc/common-components";
import {
  WorkflowCategoriesAdapter,
  WorkflowStatus,
  WorkflowTemplatesAdapter,
} from "@validereinc/domain";
import {
  Assets,
  type AssetTypeSelectionType,
} from "@validereinc/domain-controllers";
import { toTitleCaseString } from "@validereinc/utilities";
import endOfMonth from "date-fns/endOfMonth";
import subMonths from "date-fns/subMonths";
import React from "react";
import { z } from "zod";
import { WorkflowsTableFilterSchemaKeys } from "./WorkflowsFilterAreas.helpers";

const { AssetTypeSelection } = Assets.view;

export const WorkflowsViewFilterSchema = z
  .object({
    assetType: z.nativeEnum(AssetTypeSelection),
    assetName: z.string(),
    facilityId: z.array(z.string()),
    equipmentId: z.array(z.string()),
    deviceId: z.array(z.string()),
    flowId: z.array(z.string()),
    assetGroupId: z.array(z.string()),
    categoryId: z.string(),
    templateId: z.string(),
  })
  .partial();
export const WorkflowsViewFilterSchemaKeys =
  WorkflowsViewFilterSchema.keyof().Enum;

export type WorkflowsViewFiltersType = z.infer<
  typeof WorkflowsViewFilterSchema
>;

const assetTypeToAssetIdFilterPropertyMap = {
  [AssetTypeSelection.FACILITY]: WorkflowsViewFilterSchemaKeys.facilityId,
  [AssetTypeSelection.EQUIPMENT]: WorkflowsViewFilterSchemaKeys.equipmentId,
  [AssetTypeSelection.DEVICE]: WorkflowsViewFilterSchemaKeys.deviceId,
  [AssetTypeSelection.FLOW]: WorkflowsViewFilterSchemaKeys.flowId,
  [AssetTypeSelection.ASSET_GROUP]: WorkflowsViewFilterSchemaKeys.assetGroupId,
  // This is required for typing, but the asset dropdown is hidden in this case.
  [AssetTypeSelection.ALL_ASSETS]: "",
};

export const WorkflowsViewFilterAreaContent = ({
  viewConfigStorageKey,
  defaultAssetType,
  showCategoryFilter,
  showTemplateFilter,
}: Pick<StorageKeys, "viewConfigStorageKey"> & {
  defaultAssetType: AssetTypeSelectionType;
  showCategoryFilter?: boolean;
  showTemplateFilter?: boolean;
}) => {
  const { handleOnChange } = useFilterAreaContentContext();
  const { localize } = useLocalization();
  const assetTypeOptions = useGetAssetTypeOptions();

  const [filters] = useStickyState<WorkflowsViewFiltersType>(
    {} as WorkflowsViewFiltersType,
    viewConfigStorageKey
  );
  const assetType = filters.assetType ?? defaultAssetType;

  const assetSelectionInput = useAssetDropdownInput(
    assetType,
    assetTypeToAssetIdFilterPropertyMap[assetType],
    {
      isLabelShown: false,
      isFluid: false,
      isInline: true,
      description: `Filter by ${assetType === AssetTypeSelection.ALL_ASSETS ? "all assets" : `${localize(`${assetType}_plural`)}`}`,
      onChange: (val) =>
        handleOnChange(val, assetTypeToAssetIdFilterPropertyMap[assetType]),
    }
  );

  return (
    <>
      <DropdownInput
        name={WorkflowsViewFilterSchemaKeys.assetType}
        isClearable={false}
        isInline
        labelKey="label"
        valueKey="value"
        placeholder="Select Asset Type..."
        options={assetTypeOptions}
        isSortedAlphabetically={false}
        description="Filter by Asset Type"
        onChange={(val) =>
          handleOnChange(val, WorkflowsViewFilterSchemaKeys.assetType)
        }
      />
      {assetSelectionInput}
      {showCategoryFilter ? (
        <DropdownInput
          name={WorkflowsViewFilterSchemaKeys.categoryId}
          onFetchData={async (payload) => {
            let { data } = await WorkflowCategoriesAdapter.getList({
              ...payload,
              filters: {
                ...(payload.searchTerm
                  ? {
                      name: payload.searchTerm,
                    }
                  : {}),
              },
            });

            // REVIEW: not a fan of the fact that we need to do this. A refactor of DropdownInput is needed.
            if (Array.isArray(payload.value)) {
              data = data.filter((d) => payload.value.includes(d.id));
            }

            return data;
          }}
          labelKey="name"
          valueKey="id"
          placeholder="Select Category..."
          label="Workflow Category"
          description="Filter by Workflow Category"
          isOptionalTextShown={false}
          isLabelShown={false}
          isInline
          onChange={(val) =>
            handleOnChange(val, WorkflowsViewFilterSchemaKeys.categoryId)
          }
        />
      ) : null}
      {showTemplateFilter ? (
        <DropdownInput
          name={WorkflowsViewFilterSchemaKeys.templateId}
          onFetchData={async (payload) => {
            let { data } = await WorkflowTemplatesAdapter.getList({
              ...payload,
              filters: {
                ...(payload.searchTerm
                  ? {
                      name: payload.searchTerm,
                    }
                  : {}),
              },
            });

            // REVIEW: not a fan of the fact that we need to do this. A refactor of DropdownInput is needed.
            if (Array.isArray(payload.value)) {
              data = data.filter((d) => payload.value.includes(d.id));
            }

            return data;
          }}
          labelKey="name"
          valueKey="id"
          placeholder="Select Template..."
          label="Workflow Template"
          description="Filter by Workflow Template"
          isOptionalTextShown={false}
          isLabelShown={false}
          isMulti
          isInline
          onChange={(val) =>
            handleOnChange(val, WorkflowsViewFilterSchemaKeys.templateId)
          }
        />
      ) : null}
    </>
  );
};

export const WorkflowsViewFilterArea = ({
  viewConfigStorageKey,
  defaultAssetType = AssetTypeSelection.ALL_ASSETS,
  ...rest
}: Pick<StorageKeys, "viewConfigStorageKey"> & {
  defaultAssetType?: AssetTypeSelectionType;
  showCategoryFilter?: boolean;
  showTemplateFilter?: boolean;
}) => {
  return (
    <FilterArea.Root
      storageKey={viewConfigStorageKey}
      defaultValues={{
        [WorkflowsViewFilterSchemaKeys.assetType]: defaultAssetType,
      }}
      applyDefaultValues
      shouldPrioritizeStoredFiltersWhenApplyingDefaultValues
    >
      <FilterArea.Container style={{ marginBottom: 16 }}>
        <FilterArea.Content>
          {() => (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
            >
              <WorkflowsViewFilterAreaContent
                viewConfigStorageKey={viewConfigStorageKey}
                defaultAssetType={defaultAssetType}
                {...rest}
              />
            </div>
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};

const WorkflowsTableFilterAreaDrawerContent = ({
  viewConfigStorageKey,
  defaultAssetType,
}: Pick<StorageKeys, "filterConfigStorageKey" | "viewConfigStorageKey"> & {
  defaultAssetType: AssetTypeSelectionType;
}) => {
  const { localize } = useLocalization();
  const [filters] = useStickyState<WorkflowsViewFiltersType>(
    {} as WorkflowsViewFiltersType,
    viewConfigStorageKey
  );
  const assetType = filters.assetType ?? defaultAssetType;

  const assetInputNameMap: Partial<
    Record<AssetTypeSelectionType, keyof typeof WorkflowsTableFilterSchemaKeys>
  > = {
    asset_group: "assetGroupId",
    device: "deviceId",
    equipment: "equipmentId",
    facility: "facilityId",
    flow: "flowId",
  };

  const assetSelectionInput = useAssetDropdownInput(
    assetType,
    assetType ? (assetInputNameMap[assetType] ?? "") : ""
  );
  const attributeFilters = useCustomAttributeFiltersV2({
    assetType:
      assetType !== AssetTypeSelection.ALL_ASSETS
        ? assetType
        : AssetTypeSelection.FACILITY,
    prefix: `workflow.${assetType}.custom_attributes`,
  });

  return (
    <>
      <DateSelectorInput
        name={WorkflowsTableFilterSchemaKeys.date_range}
        variant="month"
        isRange
        isFluid
        label="Created At"
        description="Filter by Created At"
        isOptionalTextShown={false}
      />
      {assetType !== "all_assets" ? (
        <Accordion defaultActiveKeys={[assetType]}>
          <Accordion.AccordionPanel
            dataKey={assetType}
            title={localize(`${assetType}_plural`)}
          >
            {assetSelectionInput}
            {...attributeFilters.customAttributeFilters}
          </Accordion.AccordionPanel>
        </Accordion>
      ) : null}
    </>
  );
};

const WorkflowsStatusPillToggles = ({
  onChange,
}: {
  onChange: (val: string[]) => void;
}) => {
  const { storedFilters } = useFilterAreaContext<{
    status: string[];
  }>();
  const pillToggles = [
    {
      name: "All",
      label: "All",
      value: "",
      isSelected:
        !storedFilters.status?.length || storedFilters.status?.includes(""),
      shouldSelectAll: true,
    },
    {
      name: WorkflowStatus.COMPLETE,
      label: toTitleCaseString(WorkflowStatus.COMPLETE, { intent: "ui" }),
      value: WorkflowStatus.COMPLETE,
      variant: PillToggleVariants.GOOD,
      isSelected:
        storedFilters.status?.includes(WorkflowStatus.COMPLETE) ?? false,
    },
    {
      name: WorkflowStatus.IN_PROGRESS,
      label: toTitleCaseString(WorkflowStatus.IN_PROGRESS, {
        intent: "ui",
        exceptions: ["oil-and-gas"],
      }),
      value: WorkflowStatus.IN_PROGRESS,
      variant: PillToggleVariants.PENDING,
      isSelected:
        storedFilters.status?.includes(WorkflowStatus.IN_PROGRESS) ?? false,
    },
    {
      name: WorkflowStatus.DISMISSED,
      label: toTitleCaseString(WorkflowStatus.DISMISSED, { intent: "ui" }),
      value: WorkflowStatus.DISMISSED,
      variant: PillToggleVariants.NEUTRAL,
      isSelected:
        storedFilters.status?.includes(WorkflowStatus.DISMISSED) ?? false,
    },
    {
      name: WorkflowStatus.OVERDUE,
      label: toTitleCaseString(WorkflowStatus.OVERDUE, { intent: "ui" }),
      value: WorkflowStatus.OVERDUE,
      variant: PillToggleVariants.ATTENTION,
      isSelected:
        storedFilters.status?.includes(WorkflowStatus.OVERDUE) ?? false,
    },
    {
      name: WorkflowStatus.MISSED,
      label: toTitleCaseString(WorkflowStatus.MISSED, { intent: "ui" }),
      value: WorkflowStatus.MISSED,
      variant: PillToggleVariants.FAILURE,
      isSelected:
        storedFilters.status?.includes(WorkflowStatus.MISSED) ?? false,
    },
  ];

  return (
    <PillToggleGroup
      name="status"
      pills={pillToggles}
      onChange={(val) => onChange(val)}
    />
  );
};

export const WorkflowsTableTitleDecorationFilterArea = ({
  filterConfigStorageKey,
}: Pick<StorageKeys, "filterConfigStorageKey">) => {
  return (
    <FilterArea.Root storageKey={filterConfigStorageKey}>
      <FilterArea.Container aria-label="Filters for Workflows">
        <FilterArea.Content>
          {({ handleOnChange }) => (
            <div style={{ marginRight: 8, display: "flex", gap: 8 }}>
              <WorkflowsStatusPillToggles
                onChange={(val) =>
                  handleOnChange(val, WorkflowsTableFilterSchemaKeys.status)
                }
              />
            </div>
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};

export const WorkflowsTableFilterArea = ({
  filterConfigStorageKey,
  viewConfigStorageKey,
  defaultAssetType = AssetTypeSelection.ALL_ASSETS,
}: Pick<StorageKeys, "filterConfigStorageKey" | "viewConfigStorageKey"> & {
  defaultAssetType?: AssetTypeSelectionType;
}) => {
  return (
    <FilterArea.Root
      storageKey={filterConfigStorageKey}
      defaultValues={{
        [WorkflowsTableFilterSchemaKeys.date_range]: {
          from: subMonths(new Date(), 6),
          to: endOfMonth(new Date()),
        },
      }}
      applyDefaultValues
    >
      <FilterArea.Container aria-label="Filters for Workflows">
        <FilterDrawer.Root>
          <FilterArea.Content>
            {({ handleOnChange }) => (
              <div style={{ marginRight: 8, marginBottom: 0 }}>
                <TextInput
                  name={WorkflowsTableFilterSchemaKeys.name}
                  type="search"
                  placeholder="Search..."
                  label="Search"
                  isLabelShown={false}
                  isInline
                  onChange={(val) =>
                    handleOnChange(val, WorkflowsTableFilterSchemaKeys.name)
                  }
                />
              </div>
            )}
          </FilterArea.Content>
          <FilterDrawer.Trigger />
          <FilterDrawer.Content>
            <WorkflowsTableFilterAreaDrawerContent
              filterConfigStorageKey={filterConfigStorageKey}
              viewConfigStorageKey={viewConfigStorageKey}
              defaultAssetType={defaultAssetType}
            />
          </FilterDrawer.Content>
        </FilterDrawer.Root>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};
